/**
 * signInSecurityVocabulary — the closed lists of sign-in security: Prest0's own two-step sign-in, the dated sign-in
 * requirements, the finish-setup wall and the help an owner or admin gives a member who cannot sign in
 * (docs/PLAN-signin-requirements-20261029.md).
 *
 * One owner per vocabulary (CLAUDE.md): these lists live here and nowhere else on the server. The web reads them from
 * a byte-identical twin, src/types/signInSecurityVocabulary.ts, pinned equal by
 * tests/settings/signInSecurityVocabulary.parity.test.ts. The twin imports nothing, so both trees load it.
 *
 * The contract every requirement keeps (the design record's "contract"): announced at least REQUIREMENT_MIN_NOTICE_DAYS before it
 * is enforced, always with a named date; counted down to the person and to their workspace's owners and admins; and
 * after the date the person still signs in with what they have and lands on one finish-setup screen, never a dead end.
 */

/**
 * The custom claim that marks a setup-only session: its value is the list of REQUIREMENT_KINDS still to finish. A
 * restriction, never a grant: the rules and the session guard refuse such a session everywhere except the
 * SETUP_DOORS. Written by one door only (functions/src/auth/sessionClaims.ts); the custom-claim census skips it
 * (RESTRICTION_ONLY_CLAIMS) because it comes and goes as people finish setting up.
 */
export const SETUP_CLAIM = 'p0_setup';

/** Claims that only ever take access away. The census of who holds a custom claim ignores these and only these. */
export const RESTRICTION_ONLY_CLAIMS: readonly string[] = [SETUP_CLAIM];

/**
 * What a person may be required to have before they continue. `two-step`: an authenticator app as a second sign-in
 * step (a workspace's owner requires it). `email-confirmed`: the sign-in address proved to be theirs (the platform
 * requires it, on a date Miles sets).
 */
export const REQUIREMENT_KINDS = ['two-step', 'email-confirmed'] as const;
export type RequirementKind = (typeof REQUIREMENT_KINDS)[number];

/** Where a requirement comes from: a workspace's own policy, or the platform. */
export const REQUIREMENT_SOURCE_KINDS = ['workspace', 'platform'] as const;
export type RequirementSourceKind = (typeof REQUIREMENT_SOURCE_KINDS)[number];

/**
 * A requirement's state for one person, always derived from its dates and Firebase Auth's own record, never stored:
 * `upcoming` (more than 7 days left), `due-soon` (7 days or less), `overdue` (the date has passed and it is not done:
 * the person is on the finish-setup screen), `met`.
 */
export const REQUIREMENT_STATES = ['upcoming', 'due-soon', 'overdue', 'met'] as const;
export type RequirementState = (typeof REQUIREMENT_STATES)[number];

/** Days of notice a requirement gets at the least, from the day it is announced to the day it is enforced. */
export const REQUIREMENT_MIN_NOTICE_DAYS = 14;
/** Days a person who joins a workspace that already requires two-step gets to set it up, whatever its date. */
export const REQUIREMENT_NEW_MEMBER_GRACE_DAYS = 14;
/** The most days a workspace's helpers may add to one person's date, all extensions together. */
export const REQUIREMENT_MAX_EXTENSION_DAYS = 30;
/** The extensions a helper may give at once. */
export const REQUIREMENT_EXTENSION_STEPS_DAYS = [3, 7, 14] as const;
export type RequirementExtensionStep = (typeof REQUIREMENT_EXTENSION_STEPS_DAYS)[number];
/** Why a helper gave more time (a closed word on the audit row; never free text). */
export const REQUIREMENT_EXTENSION_REASONS = ['on-leave', 'new-phone-coming', 'other'] as const;
export type RequirementExtensionReason = (typeof REQUIREMENT_EXTENSION_REASONS)[number];
/** Days within which a requirement is `due-soon`. */
export const REQUIREMENT_DUE_SOON_DAYS = 7;

/**
 * The person's countdown, in order. `announce` goes when the requirement is set; `d7`, `d3` and `d1` that many days
 * before the date; `d0` on the morning of the date; `overdue` once, the day after. A rung whose window passed is never
 * sent late, but no rung before the consequence is ever skipped: the sweep sends the earliest one still owed.
 */
export const REQUIREMENT_PERSON_RUNGS = ['announce', 'd7', 'd3', 'd1', 'd0', 'overdue'] as const;
export type RequirementRung = (typeof REQUIREMENT_PERSON_RUNGS)[number];
/** Days before the date at which each dated rung opens (`overdue` opens a day after it). */
export const REQUIREMENT_RUNG_LEAD_DAYS: Readonly<Record<Exclude<RequirementRung, 'announce'>, number>> = {
  d7: 7,
  d3: 3,
  d1: 1,
  d0: 0,
  overdue: -1,
};
/** The rungs that go out on every channel the person has, text included, whatever their notice settings say. */
export const REQUIREMENT_FORCED_TEXT_RUNGS: readonly RequirementRung[] = ['d1', 'd0', 'overdue'];
/** The digest a workspace's owners and admins get: when it is set, a week before, the day before, and the day after. */
export const REQUIREMENT_DIGEST_RUNGS = ['announce', 'd7', 'd1', 'overdue'] as const;
export type RequirementDigestRung = (typeof REQUIREMENT_DIGEST_RUNGS)[number];

/**
 * The doors a setup-only session may still call: its own status, finishing two-step or email, redeeming a help code
 * and sending itself a confirmation email. Every other callable and endpoint refuses it.
 */
export const SETUP_DOORS = [
  'signInSecurityStatus',
  'signInSecurityConfirmTwoStep',
  'signInSecurityConfirmEmail',
  'redeemSignInHelpCode',
  'authSendVerificationEmail',
] as const;
export type SetupDoor = (typeof SETUP_DOORS)[number];

/** What the session guard answers when it refuses a call (HttpsError `details.code`). */
export const SESSION_REFUSALS = ['finish-setup', 'session-ended'] as const;
export type SessionRefusal = (typeof SESSION_REFUSALS)[number];

/** How a member signs in, as Team & Roles shows it to the people who may help them. */
export const SIGN_IN_METHODS = ['google-workspace', 'google', 'password', 'google-and-password', 'other'] as const;
export type SignInMethod = (typeof SIGN_IN_METHODS)[number];

/**
 * When a member last signed in, only ever as a coarse bucket: the account is the person's own across every workspace
 * they belong to, so an exact time would tell one workspace about their activity in another.
 */
export const LAST_SIGN_IN_BUCKETS = ['today', 'this-week', 'this-month', 'longer', 'never'] as const;
export type LastSignInBucket = (typeof LAST_SIGN_IN_BUCKETS)[number];

/**
 * A sign-in help code: what an owner or admin gives a member who lost their authenticator. It works only together with
 * the member's own password or Google sign-in (NIST SP 800-63B-4 §4.2: a recovery code plus a bound authenticator),
 * once, within HELP_CODE_TTL_MINUTES, and never signs anyone in by itself.
 */
export const HELP_CODE_DIGITS = 8;
export const HELP_CODE_TTL_MINUTES = 10;
export const HELP_CODE_MAX_TRIES = 5;
export const HELP_CODE_PER_TARGET_PER_DAY = 3;
export const HELP_CODE_PER_ISSUER_PER_DAY = 10;
/** Past this many codes in a day, one workspace raises an ops signal: an unusual run of codes is worth a look. */
export const HELP_CODE_WORKSPACE_SIGNAL_PER_DAY = 20;
/** How long the one-time setup ticket a redemption returns stays good for the new authenticator. */
export const SETUP_TICKET_TTL_MINUTES = 15;
/** How many saved recovery codes a person gets when they turn two-step on (each works once). */
export const RECOVERY_CODE_COUNT = 10;

/** What stands in for a lost authenticator at redemption: a helper's code, or one of the person's saved codes. */
export const REPLACEMENT_SOURCES = ['help-code', 'recovery-code'] as const;
export type ReplacementSource = (typeof REPLACEMENT_SOURCES)[number];

/** How the person proves their own first factor when they redeem: the password they typed, or their Google sign-in. */
export const FIRST_FACTOR_PROOFS = ['password', 'google'] as const;
export type FirstFactorProof = (typeof FIRST_FACTOR_PROOFS)[number];

/**
 * What the help doors on Team & Roles answer when they do not go ahead (HttpsError `details.code`). Each is about the
 * asker or the request, never a reason drawn from another workspace (`not-from-here` is all an asker learns there).
 */
export const SIGN_IN_HELP_REFUSALS = [
  /** The asker does not hold the help key here, or is paused. */
  'not-allowed',
  /** Nobody helps themselves. */
  'self',
  /** An owner, or someone who may help others, is helped only by an owner. */
  'needs-owner',
  /** The person is not an active member of this workspace. */
  'not-a-member',
  /** Creating a code needs a sign-in in the last 15 minutes. */
  'reauth-required',
  /** Creating a code needs the asker's own session to have used two-step. */
  'asker-needs-two-step',
  /** The person has no two-step to replace: they sign in and set it up themselves. */
  'no-two-step',
  /** A password link goes only to an account that already has a password (never a first one). */
  'no-password',
  /** This person's two-step can't be reset from this workspace (Prest0 support can help them). */
  'not-from-here',
  /** The person's date can't move further, or the extension is not one of the steps. */
  'no-more-time',
  /** Nothing is due for this person here. */
  'nothing-due',
  'too-many',
  'unavailable',
] as const;
export type SignInHelpRefusal = (typeof SIGN_IN_HELP_REFUSALS)[number];

/** What the person's own sign-in security doors answer when they do not go ahead. */
export const SIGN_IN_SECURITY_REFUSALS = [
  'unauthenticated',
  'reauth-required',
  /** Changing two-step on an account that has it needs this session to have used it. */
  'needs-two-step-session',
  /** Two-step needs the sign-in address confirmed first (Firebase's own rule for a second factor). */
  'email-unconfirmed',
  /** A workspace the person belongs to requires two-step, so it stays on. */
  'required-here',
  /** No authenticator was found on the account to confirm. */
  'not-enrolled',
  /** The setup ticket is missing, used or past its time. */
  'ticket-invalid',
  'too-many',
  'unavailable',
] as const;
export type SignInSecurityRefusal = (typeof SIGN_IN_SECURITY_REFUSALS)[number];

/**
 * What redeeming a help code or a recovery code answers when it does not go ahead. One word for every failure of the
 * code, the address or the first factor (`not-accepted`), so the door tells nobody whether an address has an account,
 * a code was ever made, or a password was right on its own.
 */
export const REDEEM_REFUSALS = ['not-accepted', 'too-many', 'unavailable'] as const;
export type RedeemRefusal = (typeof REDEEM_REFUSALS)[number];

/** What a workspace's "require two-step sign-in" setting answers when it does not go ahead. */
export const SIGN_IN_POLICY_REFUSALS = [
  /** The asker does not hold security.manage here, or is paused. */
  'not-allowed',
  'reauth-required',
  /** Requiring two-step for others needs the asker's own session to have used it. */
  'asker-needs-two-step',
  /** The date is less than REQUIREMENT_MIN_NOTICE_DAYS away (people are owed their notice). */
  'date-too-soon',
  /** The date is more than SIGN_IN_POLICY_MAX_LEAD_DAYS away. */
  'date-too-far',
  'unavailable',
] as const;
export type SignInPolicyRefusal = (typeof SIGN_IN_POLICY_REFUSALS)[number];
/** The furthest a workspace may set its two-step date. */
export const SIGN_IN_POLICY_MAX_LEAD_DAYS = 90;
